import React, { useEffect, useMemo, useState } from "react";
import { Button } from "primereact/button";
import { Calendar } from "primereact/calendar";
import { Chart } from "primereact/chart";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Dropdown } from "primereact/dropdown";
import toast from "react-hot-toast";
import {
  BenefitsMetricsLoadingOverlay,
  BenefitsMetricsRefreshingIndicator,
  BenefitsMetricsTenantScopeHeader,
} from "./components/BenefitsMetricsWidgets";
import {
  ActiveUsersMetrics,
  ActiveUsersTenantBreakdown,
} from "../../models/activeUsersMetrics";
import { TENANT_CONTEXT_ALL } from "../../store/services/api";
import { useTenantScope } from "../../hooks/useTenantScope";
import { useGetActiveUsersMetricsQuery } from "../../store/services/activity";
import { useActiveUsersExport } from "../../hooks/useActiveUsersExport";
import { useBenefitsMetricsQueryDisplay } from "../../hooks/useBenefitsMetricsQueryDisplay";
import { useTenantBrandColors } from "../../hooks/useTenantBrandColors";
import { getApiErrorMessage } from "../../utils/functions/api-errors";
import { getTenantBrandColor } from "../../utils/functions/tenantThemeColor";
import {
  CALENDAR_DISPLAY_DATE_FORMAT,
  CALENDAR_LOCALE,
} from "../../utils/constants/calendar-constants";
import {
  ACTIVE_USERS_DEFAULT_WINDOW_DAYS,
  ACTIVE_USERS_WINDOW_OPTIONS,
  defaultActiveUsersCutoffDate,
  formatActiveUsersWindowLabel,
  toActiveUsersDayParam,
} from "../../utils/functions/activeUsersWindow";
import {
  activeUsersChartOptions,
  activeUsersTenantChartOptions,
  buildActiveUsersByDayChartData,
  buildActiveUsersTenantChartData,
  buildActiveUsersTenantSeries,
  hasActiveUsersTenantBreakdown,
} from "../../utils/functions/activeUsersPresentation";

export const ActiveUsersDashboard: React.FC = () => {
  const [cutoffDate, setCutoffDate] = useState<Date>(defaultActiveUsersCutoffDate);
  const [windowDays, setWindowDays] = useState<number>(ACTIVE_USERS_DEFAULT_WINDOW_DAYS);
  const [todayDate] = useState(defaultActiveUsersCutoffDate);
  const scope = useTenantScope();
  const selectedTenantId = scope.tenantId;

  const queryParams = {
    dateTo: toActiveUsersDayParam(cutoffDate),
    windowDays,
    ...(selectedTenantId ? { tenantId: selectedTenantId } : {}),
  };

  const { data, currentData, isLoading, isFetching, isError, error } =
    useGetActiveUsersMetricsQuery(queryParams, { skip: scope.loading });

  const { metrics, isBlockingLoading, isRefreshing } =
    useBenefitsMetricsQueryDisplay<ActiveUsersMetrics>({
      data,
      currentData,
      isFetching,
      scopeKey: selectedTenantId ?? TENANT_CONTEXT_ALL,
    });

  const { exportActiveUsers, isExporting } = useActiveUsersExport();

  useEffect(() => {
    if (isError) {
      toast.error(getApiErrorMessage(error) ?? "Error al cargar usuarios activos");
    }
  }, [isError, error]);

  const activeUsersByDay = useMemo(
    () => metrics?.active_users_by_day ?? [],
    [metrics?.active_users_by_day]
  );
  const tenantBrandColors = useTenantBrandColors();
  const tenantSeries = useMemo(
    () => buildActiveUsersTenantSeries(metrics?.by_tenant),
    [metrics?.by_tenant]
  );
  const showTenantBreakdown =
    !selectedTenantId && hasActiveUsersTenantBreakdown(tenantSeries);
  const selectedTenantColor = selectedTenantId
    ? tenantBrandColors[selectedTenantId] ?? getTenantBrandColor(null)
    : null;
  const chartData = useMemo(
    () =>
      showTenantBreakdown
        ? buildActiveUsersTenantChartData(tenantSeries)
        : buildActiveUsersByDayChartData(activeUsersByDay, selectedTenantColor),
    [showTenantBreakdown, tenantSeries, activeUsersByDay, selectedTenantColor]
  );

  const tenantNameBody = (row: ActiveUsersTenantBreakdown) =>
    row.tenant_name || row.tenant_id;

  return (
    <div className="p-4">
      <div className="p-card p-4 relative overflow-hidden">
        <BenefitsMetricsLoadingOverlay visible={isBlockingLoading} />
        <BenefitsMetricsTenantScopeHeader tenantId={selectedTenantId} />

        <div className="mb-4 flex flex-wrap align-items-center gap-2">
          <h1 className="text-2xl font-semibold text-900 m-0">Usuarios activos</h1>
          <BenefitsMetricsRefreshingIndicator visible={isRefreshing} />
          <div className="ml-auto">
            <Button
              type="button"
              label="Exportar CSV"
              icon="pi pi-download"
              onClick={() => exportActiveUsers(queryParams)}
              disabled={scope.loading || isBlockingLoading || isExporting}
              loading={isExporting}
              tooltip="Una fila por usuario y día de actividad, con nombre, apellido y email. El total de activos es la cantidad de emails distintos, no la de filas."
              tooltipOptions={{ position: "left", showDelay: 300 }}
              data-testid="export-active-users-button"
            />
          </div>
        </div>

        {/* Por encima del overlay: en una carga fría el dato queda tapado, pero el
            usuario tiene que poder seguir moviendo los filtros. */}
        <div className="grid mb-4 relative" style={{ maxWidth: "480px", zIndex: 11 }}>
          <div className="col-12 sm:col-6">
            <label htmlFor="active-users-cutoff" className="block font-medium mb-2">
              Fecha de corte
            </label>
            <Calendar
              inputId="active-users-cutoff"
              className="w-full"
              inputClassName="w-full"
              value={cutoffDate}
              onChange={(e) => {
                if (e.value instanceof Date) {
                  setCutoffDate(e.value);
                }
              }}
              dateFormat={CALENDAR_DISPLAY_DATE_FORMAT}
              locale={CALENDAR_LOCALE}
              maxDate={todayDate}
              showIcon
              disabled={isLoading}
            />
          </div>
          <div className="col-12 sm:col-6">
            <label htmlFor="active-users-window" className="block font-medium mb-2">
              Ventana
            </label>
            <Dropdown
              inputId="active-users-window"
              className="w-full"
              value={windowDays}
              options={ACTIVE_USERS_WINDOW_OPTIONS}
              optionLabel="label"
              optionValue="value"
              onChange={(e) => setWindowDays(e.value as number)}
              disabled={isLoading}
            />
          </div>
        </div>

        <div className="grid mb-4">
          <div className="col-12 md:col-6">
            <div className="surface-100 border-round p-3 h-full flex flex-column">
              <div className="text-color-secondary mb-2">Usuarios activos</div>
              <div
                className="text-3xl font-bold text-900"
                data-testid="active-users-total"
              >
                {metrics?.active_users ?? "—"}
              </div>
              <small
                className="text-color-secondary block mt-2"
                style={{ minHeight: "2.5rem" }}
              >
                Usuarios distintos que abrieron la app o iniciaron sesión entre el{" "}
                {formatActiveUsersWindowLabel(cutoffDate, windowDays)}.
              </small>
            </div>
          </div>
        </div>

        <div className="mb-4">
          <h2 className="text-xl font-semibold text-900 mt-0 mb-3">
            Usuarios activos por día
          </h2>
          {activeUsersByDay.length ? (
            <div style={{ height: showTenantBreakdown ? "320px" : "280px" }}>
              <Chart
                key={`active-users-day-${selectedTenantId ?? "all"}-${
                  selectedTenantColor ?? "default"
                }`}
                type="line"
                data={chartData}
                options={
                  showTenantBreakdown
                    ? activeUsersTenantChartOptions
                    : activeUsersChartOptions
                }
                className="h-full w-full"
              />
            </div>
          ) : (
            <p className="text-color-secondary m-0">
              {isBlockingLoading
                ? ""
                : isError
                ? "No pudimos cargar los usuarios activos"
                : "Sin actividad en la ventana seleccionada"}
            </p>
          )}
        </div>

        {showTenantBreakdown && (
          <div>
            <h2 className="text-xl font-semibold text-900 mt-0 mb-3">Por tenant</h2>
            <DataTable
              value={metrics?.by_tenant ?? []}
              dataKey="tenant_id"
              emptyMessage="Sin datos"
              size="small"
            >
              <Column header="Tenant" body={tenantNameBody} />
              <Column field="active_users" header="Usuarios activos" />
            </DataTable>
            <small className="text-color-secondary block mt-2">
              Un usuario con membresía activa en dos tenants cuenta 1 en cada uno, y 1 solo
              en el total de arriba: por eso la suma de esta tabla puede ser mayor.
            </small>
          </div>
        )}
      </div>
    </div>
  );
};
